import React, { useEffect } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  Badge,
  Button,
  Divider,
  Tooltip,
  useDisclosure,
  useBreakpointValue,
} from '@chakra-ui/react';
import { UserPlus, MessageSquare, MessageSquarePlus } from 'lucide-react';
import { useOnboardingProgress } from '@/providers/onboarding-progress-provider';
import { getOnboardingStepIcon } from '@/constants/onboarding-step-icons';
import { useAppSelector } from '@/stores/store';
import { selectCurrentTeam, selectTeamLeader } from '@/stores/selectors/chat-selectors';
import { selectUser } from '@/stores/selectors/user-selectors';
import { TeamInvitationModal } from '@/components/team-invitation-modal';
import { useThreads } from '@/hooks/use-threads';

const PAGERDUTY_GREEN = '#06AC38';
const PAGERDUTY_BLUE = '#0052cc';
const GRAY_DEFAULT = '#A0AEC0';

interface ProgressSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

export const ProgressSidebar: React.FC<ProgressSidebarProps> = ({ isCollapsed, onToggle }) => {
  const { steps, currentStep } = useOnboardingProgress();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const teamLeader = useAppSelector(selectTeamLeader);
  const user = useAppSelector(selectUser);
  const stepsSubdomain = useAppSelector((state) => state.onboardingSteps.subdomain);
  const { isOpen: isInviteModalOpen, onOpen: onOpenInviteModal, onClose: onCloseInviteModal } = useDisclosure();

  // Recent threads + "New Onboarding" affordance, mirrors the
  // migration-progress-sidebar pattern so users can start a fresh
  // onboarding without leaving the assistant view.
  const {
    threads,
    currentThreadId,
    switchThread,
    fetchRecentThreads,
    newChat,
  } = useThreads();

  useEffect(() => {
    fetchRecentThreads();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatRelativeTime = (ts: number) => {
    const days = Math.floor((Date.now() - ts) / 86400000);
    if (days <= 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return new Date(ts).toLocaleDateString();
  };

  // Force collapsed on mobile, respect prop on desktop
  const isMobile = useBreakpointValue({ base: true, md: false });
  const forceCollapsed = isMobile ? true : isCollapsed;

  // Subdomain priority: steps (from thread) > currentTeam > user (from OAuth)
  const subdomain = stepsSubdomain || currentTeam?.subdomain || user?.subdomain;

  // Calculate progress percentage (exclude "end" step from calculation)
  const stepsWithoutEnd = steps.filter((step) => step.name !== 'end');
  const completedCount = stepsWithoutEnd.filter((step) => step.status === 'Complete').length;
  const progressPercentage =
    stepsWithoutEnd.length > 0 ? Math.round((completedCount / stepsWithoutEnd.length) * 100) : 0;

  // If we're at the "end" step, show 100%
  const isAtEndStep = currentStep?.name === 'end';
  const displayPercentage = isAtEndStep ? 100 : progressPercentage;

  // Get current step icon
  const getCurrentStepIcon = () => {
    if (!currentStep) {
      // Return a default icon when there's no current step
      const DefaultIcon = getOnboardingStepIcon('');
      return <DefaultIcon size={24} color={GRAY_DEFAULT} />;
    }
    const StepIcon = getOnboardingStepIcon(currentStep.name);
    const isCompleted = currentStep.status === 'Complete';
    return isCompleted ? (
      <StepIcon size={24} color={PAGERDUTY_GREEN} />
    ) : (
      <StepIcon size={20} color={PAGERDUTY_BLUE} />
    );
  };

  return (
    <Box
      w={{ base: '50px', md: forceCollapsed ? '60px' : '280px' }}
      minW={{ base: '50px', md: '100px' }}
      h="100%"
      bg="white"
      borderRight="1px solid"
      borderColor="gray.200"
      position="relative"
      transition="all 0.3s ease-in-out"
      display="flex"
      flexDirection="column"
    >
      {/* Scrollable Content Area */}
      <Box
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        p={{ base: 2, md: forceCollapsed ? 2 : 6 }}
      >
        {forceCollapsed ? (
          /* Collapsed State */
          <VStack spacing={0} align="center" pt={4}>
            {/* Progress Badge */}
            <Badge
              colorScheme="green"
              fontSize="sm"
              fontWeight="bold"
              borderRadius="full"
              px={4}
              py={1}
              minW="44px"
              textAlign="center"
              mb={2}
            >
              {displayPercentage}%
            </Badge>

            {/* Team name pill (collapsed) */}
            {currentTeam?.name && (
              <Box
                bg="blue.50"
                borderRadius="md"
                px={1}
                py={2}
                mb={3}
                maxW="44px"
                w="100%"
                title={currentTeam.name}
              >
                <Text
                  fontSize="9px"
                  color="blue.700"
                  fontWeight="bold"
                  textAlign="center"
                  noOfLines={3}
                  lineHeight="1.2"
                  wordBreak="break-word"
                >
                  {currentTeam.name}
                </Text>
              </Box>
            )}

            {/* Vertical Timeline Icons */}
            <VStack spacing={0} align="center">
              {steps.map((step, index) => {
                const isCurrentStep = currentStep?.id === step.id;
                const isCompleted = step.status === 'Complete';
                const isLastStep = index === steps.length - 1;
                const StepIcon = getOnboardingStepIcon(step.name);

                // Determine icon color based on state
                const iconColor = isCompleted
                  ? PAGERDUTY_GREEN
                  : isCurrentStep
                    ? PAGERDUTY_BLUE
                    : GRAY_DEFAULT;

                return (
                  <Box key={step.id} position="relative">
                    <Box py={4}>
                      <StepIcon size={20} color={iconColor} />
                    </Box>

                    {/* Vertical connecting line */}
                    {!isLastStep && (
                      <Box
                        position="absolute"
                        left="50%"
                        top="28px"
                        my={4}
                        w="2px"
                        h="16px"
                        bg={isCompleted ? PAGERDUTY_GREEN : 'gray.200'}
                        transform="translateX(-50%)"
                      />
                    )}
                  </Box>
                );
              })}
            </VStack>

            <Divider my={3} borderColor="gray.200" />
            <Tooltip label="New Onboarding" placement="right">
              <Box
                as="button"
                aria-label="New Onboarding"
                onClick={newChat}
                w="36px"
                h="36px"
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{ bg: 'gray.100' }}
              >
                <MessageSquarePlus size={18} color={PAGERDUTY_BLUE} />
              </Box>
            </Tooltip>
          </VStack>
        ) : (
          /* Expanded State */
          <>
            <Heading size="md" mb={4}>
              Onboarding Progress
            </Heading>

            {/* Progress Bar */}
            <Box mb={4}>
              <Flex justify="space-between" align="center" mb={2}>
                <Badge
                  colorScheme="green"
                  fontSize="sm"
                  fontWeight="bold"
                  borderRadius="full"
                  px={6}
                  py={1}
                  minW="44px"
                  textAlign="center"
                >
                  {displayPercentage}% Complete
                </Badge>
              </Flex>
            </Box>

            <Box mb={4} p={3} backgroundColor="blue.50" borderRadius="md">
              {subdomain && (
                <Text fontSize="xs" color="gray.500" mb={2}>
                  {subdomain}
                </Text>
              )}
              <Text fontSize="xs" color="gray.700" mb={1}>
                Team:
              </Text>
              <Text fontSize="sm" color="blue.700" fontWeight="bold" mb={0.5}>
                {currentTeam?.name || 'Not assigned'}
              </Text>
              {teamLeader && (
                <>
                  <Text fontSize="sm" color="blue.700" mb={0}>
                    {teamLeader.name}
                  </Text>
                  <Text fontSize="xs" color="blue.500" fontStyle="italic">
                    Team Lead
                  </Text>
                </>
              )}
            </Box>

            {/* Steps List */}
            <VStack align="stretch" spacing={0}>
              {steps.map((step, index) => {
                const isCurrentStep = currentStep?.id === step.id;
                const isCompleted = step.status === 'Complete';
                const isLastStep = index === steps.length - 1;
                const StepIcon = getOnboardingStepIcon(step.name);

                // Determine icon color based on state
                const iconColor = isCompleted
                  ? PAGERDUTY_GREEN
                  : isCurrentStep
                    ? PAGERDUTY_BLUE
                    : GRAY_DEFAULT;

                return (
                  <Box key={step.id} position="relative">
                    <Flex align="center" gap={3} py={2}>
                      <Box position="relative" py={2} flexShrink={0}>
                        <StepIcon size={20} color={iconColor} />
                      </Box>

                      <Text
                        fontSize="sm"
                        fontWeight={isCurrentStep ? 'semibold' : 'normal'}
                        color={isCompleted || isCurrentStep ? 'gray.800' : 'gray.500'}
                      >
                        {step.displayName}
                      </Text>
                    </Flex>

                    {/* Vertical connecting line */}
                    {!isLastStep && (
                      <Box
                        position="absolute"
                        left="10px"
                        top="28px"
                        my={4}
                        w="2px"
                        h="16px"
                        bg={isCompleted ? PAGERDUTY_GREEN : 'gray.200'}
                        transform="translateX(-50%)"
                      />
                    )}
                  </Box>
                );
              })}
            </VStack>

            {/* Additional Info */}
            <Box mt={8} pt={6} borderTop="1px solid" borderColor="gray.200">
              <Text fontSize="xs" color="gray.600">
                Follow the steps to complete your PagerDuty onboarding. Each step builds on the
                previous one.
              </Text>
            </Box>

            <Divider my={4} borderColor="gray.200" />

            <Flex justify="space-between" align="center" mb={3}>
              <Flex align="center">
                <MessageSquare size={13} color={GRAY_DEFAULT} />
                <Text
                  ml={2}
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="wide"
                >
                  Recent
                </Text>
              </Flex>
              <Box
                as="button"
                display="flex"
                alignItems="center"
                gap={1}
                px={2}
                py={1}
                borderRadius="md"
                fontSize="xs"
                color={PAGERDUTY_BLUE}
                fontWeight="medium"
                _hover={{ bg: 'blue.50' }}
                onClick={newChat}
              >
                <MessageSquarePlus size={12} />
                New Onboarding
              </Box>
            </Flex>

            {threads.length === 0 ? (
              <Text fontSize="xs" color="gray.400" px={2}>
                No previous conversations
              </Text>
            ) : (
              <VStack align="stretch" spacing={0}>
                {threads.slice(0, 5).map((thread) => {
                  const isActive =
                    thread.langGraphThreadId === currentThreadId;
                  return (
                    <Box
                      key={thread.langGraphThreadId}
                      as="button"
                      textAlign="left"
                      w="full"
                      px={2}
                      py={2}
                      borderRadius="md"
                      bg={isActive ? 'blue.50' : 'transparent'}
                      _hover={{ bg: isActive ? 'blue.50' : 'gray.50' }}
                      onClick={() => switchThread(thread.langGraphThreadId)}
                    >
                      <Flex align="center" gap={2}>
                        <Box flex={1} minW={0}>
                          <Text
                            fontSize="sm"
                            noOfLines={1}
                            color={isActive ? 'blue.700' : 'gray.700'}
                            fontWeight={isActive ? 'medium' : 'normal'}
                          >
                            {thread.title ?? 'Untitled'}
                          </Text>
                          <Text fontSize="xs" color="gray.400">
                            {formatRelativeTime(thread.updatedAt)}
                          </Text>
                        </Box>
                      </Flex>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </>
        )}
      </Box>

      {/* Fixed Bottom: Invite Team Button - Only show if subdomain is available */}
      {subdomain && (
        <Box
          borderTop="1px solid"
          borderColor="gray.200"
          p={2}
          bg="white"
          flexShrink={0}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          {/* Always show icon-only on mobile, respect collapse state on desktop */}
          {forceCollapsed ? (
            <Button
              size="xs"
              colorScheme="blue"
              onClick={onOpenInviteModal}
              width="40px"
              height="40px"
              minW="40px"
              variant="outline"
              opacity={0.7}
              _hover={{ opacity: 1 }}
              p={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <UserPlus size={18} />
            </Button>
          ) : (
            <Button
              size="xs"
              colorScheme="blue"
              leftIcon={<UserPlus size={14} />}
              onClick={onOpenInviteModal}
              width="full"
              height="40px"
              variant="outline"
              opacity={0.7}
              _hover={{ opacity: 1 }}
            >
              Invite Team
            </Button>
          )}
        </Box>
      )}

      {/* Toggle Button - Hidden on mobile */}
      <Box
        position="absolute"
        top="50%"
        right={0}
        transform="translateY(-50%)"
        zIndex={10}
        display={{ base: 'none', md: 'block' }}
      >
        <Box
          as="button"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={onToggle}
          w="24px"
          h="60px"
          bg="white"
          borderRadius="8px 0 0 8px"
          cursor="pointer"
          display="flex"
          alignItems="center"
          justifyContent="center"
          _hover={{ bg: 'gray.50' }}
          _active={{ bg: 'gray.100' }}
          transition="all 0.2s"
        >
          <Box w="2px" h="24px" bg="gray.400" borderRadius="full" />
        </Box>
      </Box>

      {/* Team Invitation Modal */}
      <TeamInvitationModal
        isOpen={isInviteModalOpen}
        onClose={onCloseInviteModal}
        teamName={currentTeam?.name || 'your team'}
        subdomain={subdomain || 'your-company'}
        userName={teamLeader?.name || 'a colleague'}
      />
    </Box>
  );
};
